import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  Bars3Icon,
  HomeIcon,
  ChartBarIcon,
  CogIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  BeakerIcon,
  RocketLaunchIcon,
  BriefcaseIcon,
  DocumentChartBarIcon,
  UserIcon,
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import Logo from '@/components/UI/Logo';
import useThemeStore from '@/store/themeStore';
import { useAuthStore } from '@/store/authStore';

interface LayoutProps {
  children: React.ReactNode;
}

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Trading Pairs', href: '/trading-pairs', icon: CurrencyDollarIcon },
  { name: 'Data Collection', href: '/trading-pairs/data-collection', icon: DocumentChartBarIcon },
  { name: 'Strategies', href: '/strategies', icon: ChartBarIcon },
  { name: 'Backtesting', href: '/backtesting', icon: BeakerIcon },
  { name: 'Automation', href: '/automation', icon: RocketLaunchIcon },
  { name: 'Risk Management', href: '/risk', icon: ShieldCheckIcon },
  { name: 'Analytics', href: '/analytics', icon: DocumentChartBarIcon },
  { name: 'AI Assistant', href: '/ai-assistant', icon: UserIcon },
  { name: 'Portfolio', href: '/portfolio', icon: BriefcaseIcon },
  { name: 'Paper Trading', href: '/paper-trading', icon: CurrencyDollarIcon },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useThemeStore();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Initialize theme on mount
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check token expiration periodically
  useEffect(() => {
    const checkTokenExpiration = () => {
      const token = localStorage.getItem('auth_token');
      if (token && user) {
        try {
          // Decode JWT token to check expiration (basic check)
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            
            // Check if token is expired or will expire in the next 5 minutes
            if (payload.exp && payload.exp < currentTime + 300) {
              console.warn('🔐 [AUTH] Token expired or expiring soon - logging out');
              logout();
              navigate('/login');
            }
          }
        } catch (error) {
          console.warn('🔐 [AUTH] Invalid token format - logging out');
          logout();
          navigate('/login');
        }
      }
    };

    // Check immediately on mount
    checkTokenExpiration();

    // Check every 5 minutes
    const interval = setInterval(checkTokenExpiration, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [user, logout, navigate]);

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  const getCurrentPageName = () => {
    const currentNav = navigation.find(item => isCurrentPath(item.href));
    return currentNav ? currentNav.name : 'Dashboard';
  };

  return (
    <div className="min-h-screen bg-primary-custom">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 flex z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-surface">
            {/* Mobile sidebar header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-primary-custom h-16">
              <Logo size="sm" />
              <button
                type="button"
                className="ml-auto -mr-2 flex items-center justify-center h-10 w-10 rounded-lg text-tertiary-custom hover:bg-secondary-custom transition-colors"
                onClick={() => setSidebarOpen(false)}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Mobile navigation */}
            <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = isCurrentPath(item.href);
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-500'
                        : 'text-secondary-custom hover:bg-secondary-custom hover:text-primary-custom'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-blue-500' : 'text-tertiary-custom group-hover:text-primary-custom'
                    }`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <div className="flex flex-col flex-grow bg-surface border-r border-primary-custom shadow-professional">
          {/* Sidebar header */}
          <div className="flex items-center px-6 py-3 border-b border-primary-custom h-16">
            <Logo size="sm" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            <div className="text-caption text-tertiary-custom mb-4 px-3">
              TRADING PLATFORM
            </div>
            
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = isCurrentPath(item.href);
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-3 text-body font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-50 text-blue-700 shadow-sm border-l-4 border-blue-500'
                      : 'text-secondary-custom hover:bg-secondary-custom hover:text-primary-custom'
                  }`}
                >
                  <Icon className={`mr-3 h-5 w-5 flex-shrink-0 ${
                    isActive ? 'text-blue-500' : 'text-tertiary-custom group-hover:text-primary-custom'
                  }`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Sidebar footer */}
          <div className="flex-shrink-0 px-4 py-4 border-t border-primary-custom">
            <div className="flex items-center justify-center">
              <span className="text-caption text-tertiary-custom">Money Machine v1.0</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top header */}
        <div className="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-surface border-b border-primary-custom shadow-sm">
          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden px-4 text-tertiary-custom hover:text-primary-custom focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Header content */}
          <div className="flex-1 px-4 md:px-8 flex justify-between items-center">
            {/* Page title */}
            <div className="flex items-center">
              <h1 className="text-heading-2 text-primary-custom font-semibold">
                {getCurrentPageName()}
              </h1>
            </div>

            {/* Header actions */}
            <div className="flex items-center space-x-4">

              {/* User menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-2 rounded-lg text-tertiary-custom hover:bg-secondary-custom hover:text-primary-custom transition-colors"
                >
                  <div className="h-8 w-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.firstName?.[0] || user?.email?.[0] || 'U'}
                    </span>
                  </div>
                  <ChevronDownIcon className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-surface rounded-lg shadow-elevated border border-primary-custom z-50">
                    <div className="p-4 border-b border-primary-custom">
                      <div className="text-body font-medium text-primary-custom">
                        {user?.firstName} {user?.lastName}
                      </div>
                      <div className="text-caption text-secondary-custom">
                        {user?.email}
                      </div>
                    </div>
                    
                    <div className="py-2">
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-body text-secondary-custom hover:bg-secondary-custom hover:text-primary-custom transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <CogIcon className="h-4 w-4 mr-3" />
                        Settings
                      </Link>
                      
                      <button
                        onClick={() => {
                          toggleTheme();
                        }}
                        className="w-full flex items-center px-4 py-2 text-body text-secondary-custom hover:bg-secondary-custom hover:text-primary-custom transition-colors"
                      >
                        {theme === 'light' ? (
                          <MoonIcon className="h-4 w-4 mr-3" />
                        ) : (
                          <SunIcon className="h-4 w-4 mr-3" />
                        )}
                        {theme === 'light' ? 'Dark Theme' : 'Light Theme'}
                      </button>
                      
                      <div className="border-t border-primary-custom my-2"></div>
                      
                      <button
                        onClick={() => {
                          logout();
                          setIsUserMenuOpen(false);
                          navigate('/login');
                        }}
                        className="w-full flex items-center px-4 py-2 text-body text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="p-4 md:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 